// src/components/unlock-faster-step/UnlockFasterStep.tsx
import { useMemo } from 'react';
import { useTheme } from '../../../../contexts/theme-context';
import {
  ContentSection,
  ContinueButton,
  SkipButtonWithBorder,
} from '../secure-your-account/secure-your-account.styled';
import { useBiometricSetup } from '../../../../hooks/use-biometric-setup';
import type { OnboardingStepProps } from '../../registration';
import { detectBiometricType } from '../../../../helpers/biometric';
import { isNative } from '../../../../platform';
import { BiometricsStorage } from '../../../../helpers/biometrics-storage';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { Heading, SubHeading } from './unlock-faster.styled';
import { ENVIRONMENT } from '../../../../env';
import { UniversalSpinner } from '../../../../components/universal-spinner/universal-spinner';

const isDev = ENVIRONMENT === 'DEV';

// function computeCtaLabel() {
//   const ua = navigator.userAgent || '';
//   const isWindows = /Windows/i.test(ua);
//   const isMac = /Macintosh|Mac OS X/i.test(ua);
//   const isIPhone = /iPhone|iPad|iPod/i.test(ua);

//   if (isWindows) return 'Enable passkey';
//   if (isIPhone) return 'Enable Face ID / Touch ID';
//   if (isMac) return 'Enable Touch ID';
//   return 'Enable passkey';
// }

export const UnlockFasterStep: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { isLight } = useTheme();
  const {
    status,
    error,
    errorType,
    handleSetup,
    isLoading,
    platformAvailable,
    secureContext,
    conditionalMediationAvailable,
  } = useBiometricSetup();
  const { setRegistration } = useAuth();

  const onEnable = async () => {
    // On native, skip secure context check — native plugin handles security
    if (!isNative() && !secureContext) {
      if (isDev) {
        console.warn('[WebAuthn] Not a secure context (https/localhost required).');
      }
      return;
    }

    if (platformAvailable === false && isDev) {
      console.warn('[WebAuthn] No platform authenticator available.');
      // user may still try
    }

    const ok = await handleSetup();

    if (ok) {
      // handleSetup() already stored the correct biometryType in BiometricsStorage.
      // Read it back rather than using the UA-based heuristic (which can be wrong).
      const biometricType = BiometricsStorage.getBiometricsType() ?? detectBiometricType();
      setRegistration({ biometricsEnabled: true, biometricType, autoPromptPasskey: true });

      // handleSetup() already called setBiometricsEnabled + setBiometricsType;
      // only set auto-prompt here (not set by the hook).
      BiometricsStorage.setAutoPromptPasskey(true);

      if (isDev) {
        console.info('[WebAuthn] Biometrics enabled:', biometricType);
      }

      goNext();
    }
  };

  const onSkip = () => {
    // User skipped biometrics - explicitly set to false and disable auto-prompt
    setRegistration({ biometricsEnabled: false, autoPromptPasskey: false });

    // Store in localStorage so future logins know to skip biometrics
    BiometricsStorage.setBiometricsEnabled(false);
    BiometricsStorage.setAutoPromptPasskey(false);

    goNext();
  };

  const hint = useMemo(() => {
    if (!secureContext) return 'Needs HTTPS (or localhost) — open the app over https://';
    if (platformAvailable === false) return 'No compatible device authenticator available.';
    if (conditionalMediationAvailable === false)
      return 'Biometric autofill is not available in this browser (registration still works).';
    return null;
  }, [secureContext, platformAvailable, conditionalMediationAvailable]);

  // const ctaLabel = useMemo(() => computeCtaLabel(), []);

  return (
    <ContentSection data-testid="ob-bio">
      <VSpace $h={32} />
      <div>
        <Heading isLight={isLight} data-testid="ob-bio-heading">
          Unlock faster
          <br />
          with biometrics.
        </Heading>

        <VSpace $h={16} />

        <SubHeading data-testid="ob-bio-subheading">
          Use the fingerprint or facial recognition capabilities on your device to access <br />
          Mailander even faster – and to further
          <br /> enhance your app security.
        </SubHeading>
      </div>

      {(status || error || hint) && (
        <div
          role="status"
          data-testid="ob-bio-status"
          style={{
            borderRadius: 12,
            padding: '10px 14px',
            border: `1px solid ${
              errorType === 'unavailable'
                ? 'rgba(94, 132, 237, 0.3)'
                : errorType === 'network-or-server'
                  ? 'rgba(214, 95, 70, 0.3)'
                  : 'rgba(255, 255, 255, 0.1)'
            }`,
            background:
              errorType === 'unavailable'
                ? 'rgba(94, 132, 237, 0.1)'
                : errorType === 'network-or-server'
                  ? 'rgba(214, 95, 70, 0.1)'
                  : 'rgba(255, 255, 255, 0.05)',
            color:
              errorType === 'unavailable'
                ? '#8FAAF0'
                : errorType === 'network-or-server'
                  ? '#E88B7A'
                  : '#B7BDCB',
            fontSize: 13,
            fontFamily: 'Inter',
            whiteSpace: 'pre-line',
            lineHeight: '1.5',
          }}
        >
          {status && !error && <div style={{ marginBottom: 6 }}>{status}</div>}
          {error && errorType !== 'user-cancelled' && (
            <div style={{ fontWeight: 600 }}>{error}</div>
          )}
          {hint && !error && <div style={{ opacity: 0.9 }}>{hint}</div>}
        </div>
      )}

      <div
        style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '16px' }}
        data-testid="ob-bio-body"
      >
        <ContinueButton
          data-testid="ob-bio-enable"
          onClick={onEnable}
          disabled={isLoading || (!isNative() && !secureContext)}
        >
          {isLoading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <UniversalSpinner />
              Enabling…
            </span>
          ) : error && errorType !== 'user-cancelled' ? (
            'Try again'
          ) : (
            'Enable TouchID/FaceID'
          )}
        </ContinueButton>

        <SkipButtonWithBorder data-testid="ob-bio-skip" onClick={onSkip}>
          Skip for now
        </SkipButtonWithBorder>
      </div>
    </ContentSection>
  );
};

/* ───────── shell flags ───────── */

(UnlockFasterStep as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
(UnlockFasterStep as any).fill = false;
(UnlockFasterStep as any).smallLogo = true;
(UnlockFasterStep as any).progress = 'Security';
